import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    subject: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Prevent message from exceeding 500 characters
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic client‑side validation
    if (!formData.subject || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('subject', formData.subject);
      body.append('email', formData.email);
      body.append('message', formData.message);

      const res = await fetch(
        'https://readdy.ai/api/form/d6dvb6aohb161tfd8p2g',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        }
      );

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ subject: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              お問い合わせ
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              ご質問やご相談がございましたら、下記フォームよりお気軽にお問い合わせください
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
              <div className="lg:col-span-3">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  メールでのお問い合わせ
                </h2>
                <p className="text-gray-500 text-sm mb-10">
                  以下のフォームにご記入のうえ送信してください。担当者より折り返しご連絡いたします。
                </p>

                {submitStatus === 'success' && (
                  <div className="mb-8 p-5 bg-primary-50 border border-primary-200 rounded-2xl flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-xl text-white"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        送信が完了しました
                      </p>
                      <p className="text-gray-600 text-xs mt-0.5">
                        お問い合わせいただきありがとうございます。担当者より折り返しご連絡いたします。
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-8 p-5 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-full flex-shrink-0">
                      <i className="ri-error-warning-line text-xl text-white"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        送信に失敗しました
                      </p>
                      <p className="text-gray-600 text-xs mt-0.5">
                        恐れ入りますが、時間をおいて再度お試しください。
                      </p>
                    </div>
                  </div>
                )}

                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-semibold text-gray-800 mb-2"
                    >
                      件名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="お問い合わせの件名をご入力ください"
                      className="w-full px-5 py-3.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-800 mb-2"
                    >
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="example@email.com"
                      className="w-full px-5 py-3.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-gray-800 mb-2"
                    >
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="お問い合わせ内容をご入力ください"
                      className="w-full px-5 py-3.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none bg-gray-50 hover:bg-white"
                    />
                    <p className="text-xs text-gray-400 mt-1.5 text-right">
                      {formData.message.length} / 500文字
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        送信中...
                      </span>
                    ) : (
                      '送信する'
                    )}
                  </button>
                </form>
              </div>

              <div className="lg:col-span-2">
                <div className="sticky top-32 space-y-6">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-8 rounded-3xl text-white">
                    <h3 className="text-2xl font-bold mb-6">お問い合わせ先</h3>
                    <div className="space-y-5">
                      <a
                        href="mailto:cr.toranomon@gmail.com"
                        className="flex items-start gap-4 group cursor-pointer"
                      >
                        <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0 group-hover:bg-white/30 transition-all">
                          <i className="ri-mail-line text-xl"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1">メール</p>
                          <p className="text-primary-100 text-sm group-hover:text-white transition-colors">
                            cr.toranomon@gmail.com
                          </p>
                        </div>
                      </a>

                      <a
                        href="tel:0335881111"
                        className="flex items-start gap-4 group cursor-pointer"
                      >
                        <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0 group-hover:bg-white/30 transition-all">
                          <i className="ri-phone-line text-xl"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1">電話</p>
                          <p className="text-primary-100 text-sm group-hover:text-white transition-colors">
                            虎の門病院 地域連携室
                          </p>
                          <p className="text-primary-100 text-sm group-hover:text-white transition-colors">
                            03-3588-1111（代表）
                          </p>
                        </div>
                      </a>

                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                          <i className="ri-time-line text-xl"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1">対応時間</p>
                          <p className="text-primary-100 text-sm">
                            平日 9:00 〜 17:00
                          </p>
                          <p className="text-primary-200 text-xs mt-1">
                            ※土日祝日を除く
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                          <i className="ri-calendar-line text-xl"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1">返信目安</p>
                          <p className="text-primary-100 text-sm">
                            1週間程度
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 診療相談フォーム案内 */}
                  <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-3xl border border-teal-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl mb-4">
                      <i className="ri-stethoscope-line text-2xl text-teal-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      診療相談フォーム
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      循環器センターでは心臓や大動脈の手術などに関してお悩みのある方のために、メール相談を受け付けております。内科・外科どちらに関する内容でも構いません。内容に応じて、内科・外科の医師が回答いたします。
                    </p>
                    <p className="text-gray-500 text-xs mb-4">
                      ※虎の門病院ホームページの診療相談フォーム（循環器センター内科・外科共通）からご利用いただけます。
                    </p>
                    <a
                      href="https://req.qubo.jp/toranomon/form/ibYhyUmq"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-external-link-line text-lg"></i>
                      診療相談フォームへ
                    </a>
                  </div>

                  <div className="bg-medical-beige p-8 rounded-3xl">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                      <i className="ri-information-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      お問い合わせの前に
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 text-sm"></i>
                        <span className="text-gray-600 text-sm leading-relaxed">
                          心不全に関する一般的な情報は「心不全とは」ページをご覧ください
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 text-sm"></i>
                        <span className="text-gray-600 text-sm leading-relaxed">
                          医療機関の方は「地域連携」ページもご参照ください
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 text-sm"></i>
                        <span className="text-gray-600 text-sm leading-relaxed">
                          緊急の場合は最寄りの医療機関へご連絡ください
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                アクセス
              </h2>
              <p className="text-gray-600 text-lg">
                虎の門病院へのアクセス情報
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="bg-white p-8 rounded-3xl shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  虎の門病院
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 mb-1">住所</p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        〒105-8470<br />
                        東京都港区虎ノ門2-2-2
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                      <i className="ri-phone-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 mb-1">代表番号</p>
                      <a 
                        href="tel:0335881111"
                        className="text-primary-600 text-sm hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        03-3588-1111
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                      <i className="ri-train-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-gray-900 mb-2">最寄り駅</p>
                      <ul className="space-y-1.5 text-gray-600 text-sm">
                        <li>• 東京メトロ銀座線「虎ノ門駅」徒歩3分</li>
                        <li>• 東京メトロ日比谷線「虎ノ門ヒルズ駅」徒歩5分</li>
                        <li>• 東京メトロ南北線「六本木一丁目駅」徒歩8分</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-3xl shadow-sm overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.0234567890123!2d139.74567890000002!3d35.66789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188b8e8e8e8e8f%3A0x1234567890abcdef!2z6JmO44Gu6ZaA55eF6Zmi!5e0!3m2!1sja!2sjp!4v1234567890123!5m2!1sja!2sjp&q=%E6%9D%B1%E4%BA%AC%E9%83%BD%E6%B8%AF%E5%8C%BA%E8%99%8E%E3%83%8E%E9%96%802-2-2"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-2xl"
                  title="虎の門病院の地図"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
